import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from './Toast';
import { useState } from 'react';
import styled from 'styled-components';
import authService from '../services/authService';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1120 0%, #1a1a2e 100%);
  color: white;
  padding: 6rem 1.5rem 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SignupCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 1.5rem;
  padding: 3rem;
  max-width: 450px;
  width: 100%;
  
  @media (max-width: 640px) {
    padding: 2rem;
  }
`;

const Logo = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 2rem;
  
  &::before {
    content: '📱';
    display: block;
    font-size: 3rem;
    margin-bottom: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: white;
  font-size: 0.95rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  
  a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Benefits = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 2rem;
  
  h3 {
    color: #3b82f6;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      font-size: 0.85rem;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      
      &::before {
        content: '✓';
        color: #10b981;
        font-weight: bold;
      }
    }
  }
`;

function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';
  const plan = searchParams.get('plan') || 'lite';
  const { login } = useAuth();
  const { addToast } = useToast();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Validation des mots de passe
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      addToast('Les mots de passe ne correspondent pas', { type: 'error' });
      setIsLoading(false);
      return;
    }
    
    // Validation email ou téléphone requis
    if (!formData.email && !formData.phone) {
      setError('Veuillez fournir un email ou un numéro de téléphone');
      addToast('Veuillez fournir un email ou un numéro de téléphone', { type: 'error' });
      setIsLoading(false);
      return;
    }
    
    try {
      // Créer le compte avec authService
      const response = await authService.register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        password_confirm: formData.confirmPassword
      });
      
      // Message de succès pour la création
      addToast('Compte créé avec succès !', { type: 'success' });
      
      // Se connecter automatiquement
      const identifier = formData.email || formData.phone;
      await login({
        identifier: identifier,
        password: formData.password
      });
      
      // Message de succès pour la connexion
      addToast('Connecté automatiquement !', { type: 'success' });
      
      // Petit délai pour s'assurer que l'état est mis à jour
      setTimeout(() => {
        navigate(redirect);
      }, 100);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Erreur lors de l\'inscription';
      setError(errorMessage);
      addToast(errorMessage, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <PageWrapper>
      <SignupCard>
        <Logo>INZA STORE</Logo>
        <Title>Créer votre compte</Title>
        <Subtitle>
          {plan === 'premium' 
            ? 'Rejoignez le Club INZA Premium et profitez de tous les avantages'
            : 'Rejoignez INZA STORE et accédez aux offres exclusives'
          }
        </Subtitle>
        
        <Benefits>
          <h3>🎁 Vos avantages INZA</h3>
          <ul>
            <li>Accès aux offres flash exclusives</li>
            <li>Programme de fidélité par points</li>
            <li>Livraison gratuite sur les membres Premium</li>
            <li>Suivi de commande en temps réel</li>
            <li>Support client prioritaire</li>
          </ul>
        </Benefits>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '1.5rem',
            color: '#ef4444',
            fontSize: '0.9rem',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="first_name">Prénom</Label>
            <Input
              type="text"
              id="first_name"
              name="first_name"
              placeholder="Jean"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="last_name">Nom</Label>
            <Input
              type="text"
              id="last_name"
              name="last_name"
              placeholder="Dupont"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="jean.dupont@email.com"
              value={formData.email}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Téléphone (optionnel)</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              placeholder="06 12 34 56 78"
              value={formData.phone}
              onChange={handleChange}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Mot de passe</Label>
            <Input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <Input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={6}
            />
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Inscription...' : `Créer mon compte ${plan === 'premium' ? 'Premium' : ''}`}
          </SubmitButton>
        </Form>

        <LoginLink>
          Déjà membre ? <a href={`/login?redirect=${encodeURIComponent(redirect)}`}>Se connecter</a>
        </LoginLink>
      </SignupCard>
    </PageWrapper>
  );
}

export default SignupPage;
